import {alpha, forDarkSurface, darken, lighten, readableOn} from "./color-utils";

/**
 * The console's design tokens.
 *
 * Design intent — what makes this read as "premium" rather than "default theme + a bright colour":
 *
 *  1. **Layered surfaces, not flat.** Depth comes from three neutral steps (canvas → container →
 *     elevated), so cards sit *on* the page instead of dissolving into it. Near-black flat panels
 *     are the single biggest reason a dark UI looks cheap.
 *  2. **Neutrals with a cool undertone.** Pure #000/#fff greys look harsh and slightly dirty; a few
 *     degrees of blue in the dark ramp and a hint of warmth off pure white in the light ramp is what
 *     expensive interfaces actually use.
 *  3. **Hairlines, not borders.** Low-opacity white/black overlays instead of solid grey lines, so
 *     structure is felt rather than drawn.
 *  4. **The accent is rationed.** The brand colour is for the one thing that matters on a screen
 *     (primary action, active nav). Everywhere else uses neutrals. On dark it is lifted and slightly
 *     desaturated by `forDarkSurface` so it stops shouting.
 *  5. **Explicit text hierarchy.** Three emphasis levels rather than one flat colour, which is what
 *     lets dense data screens stay scannable.
 *
 * Everything derives from the admin-configured brand colour, so a rebrand re-themes the console
 * without touching this file.
 */

/**
 * Brand book.
 *
 *   #6acc6f  main            — the green everything keys off
 *   #fcfaf5  warm off-white  — the light canvas; a *warm* neutral, which is why the whole light
 *                              ramp below is warm rather than the usual cool blue-grey
 *   #d6eada  pale mint       — the soft accent: selected nav, tinted fills, hover
 *   black                    — text, and the base the dark ramp is lifted from
 */
export const BRAND_BOOK = {
    main: "#6acc6f",
    cream: "#fcfaf5",
    mint: "#d6eada",
    black: "#000000",
};

/**
 * Semantic colours, tuned per mode. Success deliberately sits a little away from the brand green
 * (a shade cooler and deeper) so a "success" state never reads as just another brand element.
 */
const STATUS = {
    light: {success: "#2e9e57", warning: "#c07a12", error: "#cf3b45", info: "#3070d6"},
    dark: {success: "#5fd08a", warning: "#e0a341", error: "#f2646c", info: "#74a9f0"},
};

export function buildTheme(mode, brandHex) {
    const isDark = mode === "dark";
    const brand = brandHex || BRAND_BOOK.main;
    // The accent actually used in the UI — the raw brand on light, a lifted variant on dark.
    const accent = isDark ? forDarkSurface(brand) : brand;
    const status = isDark ? STATUS.dark : STATUS.light;

    const surfaces = isDark
        ? {
              // Lifted off the brand's black with a faint green undertone, so the dark ramp belongs
              // to the same family as the green rather than fighting it with a blue-grey.
              canvas: "#0b0e0c",
              container: "#141814",
              elevated: "#1b201c",
              sunken: "#0e120f",
              hairline: "rgba(255, 255, 255, 0.075)",
              hairlineStrong: "rgba(255, 255, 255, 0.13)",
              rowAlt: "rgba(255, 255, 255, 0.026)",
              hover: "rgba(255, 255, 255, 0.055)",
              text: "rgba(255, 255, 255, 0.92)",
              textSecondary: "rgba(255, 255, 255, 0.62)",
              textTertiary: "rgba(255, 255, 255, 0.40)",
              textQuaternary: "rgba(255, 255, 255, 0.26)",
          }
        : {
              // The brand's warm off-white is the canvas; pure white containers lift off it. The
              // whole neutral ramp is warm (black tinted toward the cream) so nothing reads cold
              // next to it — a cool grey beside #fcfaf5 looks like a mistake.
              canvas: BRAND_BOOK.cream,
              container: "#ffffff",
              elevated: "#ffffff",
              sunken: "#f6f3ec",
              hairline: "rgba(38, 34, 28, 0.09)",
              hairlineStrong: "rgba(38, 34, 28, 0.15)",
              rowAlt: "rgba(38, 34, 28, 0.024)",
              hover: "rgba(38, 34, 28, 0.045)",
              text: "rgba(23, 26, 23, 0.92)",
              textSecondary: "rgba(23, 26, 23, 0.62)",
              textTertiary: "rgba(23, 26, 23, 0.44)",
              textQuaternary: "rgba(23, 26, 23, 0.28)",
          };

    /**
     * The soft accent — a tint of the brand used for selected nav, hover fills and highlights.
     * On light it is the brand book's pale mint; on dark a translucent brand wash, because a pale
     * mint would glare against the near-black canvas.
     */
    const accentSoft = isDark ? alpha(accent, 0.17) : BRAND_BOOK.mint;
    const accentSoftHover = isDark ? alpha(accent, 0.24) : darken(BRAND_BOOK.mint, 0.05);
    /** Readable brand-coloured text: the raw green is too light for body text on the cream. */
    const accentText = isDark ? accent : darken(accent, 0.16);

    // Soft, layered shadows. In dark mode shadows do little, so elevation is carried by the
    // surface step instead and the shadow only deepens the edge.
    const shadow = isDark
        ? "0 1px 2px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.36)"
        : "0 1px 2px rgba(16,19,26,0.04), 0 8px 24px rgba(16,19,26,0.06)";

    return {
        token: {
            // --- brand ---
            colorPrimary: accent,
            colorLink: accentText,
            colorLinkHover: isDark ? lighten(accent, 0.08) : darken(accent, 0.08),
            colorPrimaryHover: isDark ? lighten(accent, 0.07) : darken(accent, 0.06),
            colorPrimaryActive: isDark ? lighten(accent, 0.13) : darken(accent, 0.12),
            colorPrimaryBg: accentSoft,
            colorPrimaryBgHover: accentSoftHover,
            colorPrimaryBorder: alpha(accent, isDark ? 0.36 : 0.3),

            // --- semantic ---
            colorSuccess: status.success,
            colorWarning: status.warning,
            colorError: status.error,
            colorInfo: status.info,

            // --- surfaces ---
            colorBgLayout: surfaces.canvas,
            colorBgContainer: surfaces.container,
            colorBgElevated: surfaces.elevated,
            colorBgSpotlight: surfaces.elevated,
            colorFillAlter: surfaces.rowAlt,
            colorFillSecondary: surfaces.hover,
            colorFillTertiary: surfaces.rowAlt,
            colorFillQuaternary: surfaces.rowAlt,
            colorBgMask: isDark ? "rgba(0,0,0,0.65)" : "rgba(16,19,26,0.45)",

            // --- lines ---
            colorBorder: surfaces.hairlineStrong,
            colorBorderSecondary: surfaces.hairline,

            // --- text ---
            colorText: surfaces.text,
            colorTextSecondary: surfaces.textSecondary,
            colorTextTertiary: surfaces.textTertiary,
            colorTextQuaternary: surfaces.textQuaternary,
            colorTextHeading: surfaces.text,
            colorTextDescription: surfaces.textSecondary,
            colorTextPlaceholder: surfaces.textTertiary,

            // --- shape & type ---
            // Slightly larger radii and generous line-height read as considered rather than default.
            borderRadius: 10,
            borderRadiusLG: 14,
            borderRadiusSM: 7,
            fontFamily: 'Tahoma, "Segoe UI", system-ui, -apple-system, sans-serif',
            fontSize: 14,
            lineHeight: 1.65,
            controlHeight: 38,
            wireframe: false,

            boxShadow: shadow,
            boxShadowSecondary: shadow,
            boxShadowTertiary: isDark
                ? "0 1px 2px rgba(0,0,0,0.4)"
                : "0 1px 2px rgba(16,19,26,0.05)",
        },
        components: {
            Layout: {
                bodyBg: surfaces.canvas,
                headerBg: surfaces.container,
                siderBg: surfaces.container,
                headerHeight: 64,
            },
            Card: {
                colorBgContainer: surfaces.container,
                // Cards lift with a hairline + soft shadow rather than a hard grey outline.
                boxShadowTertiary: shadow,
                headerBg: "transparent",
                paddingLG: 20,
            },
            Menu: {
                itemBg: "transparent",
                subMenuItemBg: "transparent",
                // The active nav item is a tinted pill, not a saturated brand slab — far calmer
                // while still being unmistakably the current page.
                itemSelectedBg: accentSoft,
                itemSelectedColor: accentText,
                itemHoverBg: surfaces.hover,
                itemHoverColor: surfaces.text,
                itemColor: surfaces.textSecondary,
                itemBorderRadius: 9,
                itemMarginInline: 8,
                itemHeight: 42,
            },
            Table: {
                headerBg: isDark ? "rgba(255,255,255,0.035)" : "rgba(16,19,26,0.028)",
                headerColor: surfaces.textSecondary,
                headerSplitColor: "transparent",
                rowHoverBg: surfaces.hover,
                borderColor: surfaces.hairline,
                cellPaddingBlock: 13,
            },
            Button: {
                primaryShadow: "none",
                defaultShadow: "none",
                dangerShadow: "none",
                fontWeight: 500,
            },
            Input: {
                colorBgContainer: isDark ? surfaces.sunken : surfaces.container,
                activeShadow: `0 0 0 3px ${alpha(accent, 0.16)}`,
                hoverBorderColor: alpha(accent, 0.5),
            },
            Select: {
                colorBgContainer: isDark ? surfaces.sunken : surfaces.container,
                optionSelectedBg: accentSoft,
                optionSelectedColor: accentText,
            },
            Modal: {contentBg: surfaces.elevated, headerBg: surfaces.elevated},
            Drawer: {colorBgElevated: surfaces.elevated},
            Tag: {defaultBg: surfaces.rowAlt, defaultColor: surfaces.textSecondary},
            Tooltip: {colorBgSpotlight: isDark ? "#252a33" : "#10131a"},
            Segmented: {itemSelectedBg: accentSoft, itemSelectedColor: accentText},
            Statistic: {contentFontSize: 26, titleFontSize: 13},
            Pagination: {itemActiveBg: accentSoft},
        },
        /** Mirrored to CSS custom properties for the handful of places that can't read tokens. */
        cssVars: {
            "--npe-canvas": surfaces.canvas,
            "--npe-surface": surfaces.container,
            "--npe-surface-elevated": surfaces.elevated,
            "--npe-surface-sunken": surfaces.sunken,
            "--npe-border": surfaces.hairlineStrong,
            "--npe-hairline": surfaces.hairline,
            "--npe-row-alt": surfaces.rowAlt,
            "--npe-hover": surfaces.hover,
            "--npe-text": surfaces.text,
            "--npe-text-secondary": surfaces.textSecondary,
            "--npe-text-tertiary": surfaces.textTertiary,
            "--npe-accent": accent,
            "--npe-accent-soft": accentSoft,
            "--npe-accent-text": accentText,
            "--npe-on-accent": readableOn(accent),
            "--npe-shadow": shadow,
        },
    };
}
